import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../services/authService';
import { tokenStorage } from '../utils/tokenStorage';
import { store } from '../../store';
import { setSsoProfile, clearSsoProfile } from '../store/ssoUserSlice';
import type { SsoUserProfile } from '../types/auth';

interface AuthContextType {
  user: SsoUserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNetworkError: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SsoUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const isNetworkFailure = (err: unknown) =>
    err instanceof TypeError && err.message.toLowerCase().includes('fetch');

  const initAuth = () => {
    const accessToken = tokenStorage.getAccessToken();

    if (accessToken && !tokenStorage.isExpired()) {
      authService
        .getMe(accessToken)
        .then((profile) => {
          setUser(profile);
          store.dispatch(setSsoProfile(profile));
        })
        .catch((err) => {
          if (isNetworkFailure(err)) { setIsNetworkError(true); return; }
          tokenStorage.clear();
          store.dispatch(clearSsoProfile());
        })
        .finally(() => setIsLoading(false));
    } else if (accessToken && tokenStorage.isExpired()) {
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) {
        authService
          .refreshToken(refreshToken)
          .then((data) => {
            tokenStorage.setTokens(data.access_token, data.refresh_token, data.expires_in);
            return authService.getMe(data.access_token);
          })
          .then((profile) => {
            setUser(profile);
            store.dispatch(setSsoProfile(profile));
          })
          .catch((err) => {
            if (isNetworkFailure(err)) { setIsNetworkError(true); return; }
            tokenStorage.clear();
            store.dispatch(clearSsoProfile());
          })
          .finally(() => setIsLoading(false));
      } else {
        tokenStorage.clear();
        store.dispatch(clearSsoProfile());
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // On the callback route, AuthCallbackPage has not yet stored tokens — it
    // will dispatch a 'mopsc:auth-ready' event once tokens are saved, which
    // triggers initAuth below. Skip the normal init path here to avoid a
    // redundant (and failing) getMe() with no token.
    if (window.location.pathname.includes('/auth/callback')) {
      setIsLoading(false);
      return;
    }

    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // AuthCallbackPage fires this event after storing tokens so we can fetch
    // the user profile exactly once without relying on a re-mount.
    const onAuthReady = () => {
      setIsLoading(true);
      initAuth();
    };
    window.addEventListener('mopsc:auth-ready', onAuthReady);
    return () => window.removeEventListener('mopsc:auth-ready', onAuthReady);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    store.dispatch(clearSsoProfile());
    authService.logout();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isNetworkError,
        login: authService.login,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
